import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/shadcn/button";
import { Input } from "@/components/ui/shadcn/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/shadcn/dialog";
import { usersAPI } from "@/services/api";
import { PasswordChangeRequest } from "@/types/auth";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangePasswordDialog({
  open,
  onOpenChange,
}: ChangePasswordDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [passwordChange, setPasswordChange] = useState<PasswordChangeRequest & { confirm_password: string }>({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [error, setError] = useState<string | null>(null);

  // Use useEffect to validate passwords whenever they change
  useEffect(() => {
    const { new_password, confirm_password } = passwordChange;

    // Clear error if either field is empty
    if (!new_password || !confirm_password) {
      setError(null);
      return;
    }

    // Check if passwords match
    if (new_password !== confirm_password) {
      setError("Passwords do not match");
      return;
    }

    // Check password length
    if (new_password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    // If we get here, validation passed
    setError(null);
  }, [passwordChange.new_password, passwordChange.confirm_password]);

  const handleChangePassword = async () => {
    if (!passwordChange.current_password || !passwordChange.new_password || !passwordChange.confirm_password) {
      toast({
        title: "Validation Error",
        description: "Please fill in all fields",
        variant: "error",
      });
      return;
    }

    if (error) {
      return;
    }

    setLoading(true);
    try {
      await usersAPI.changePassword({
        current_password: passwordChange.current_password,
        new_password: passwordChange.new_password,
      });
      showSuccessToast(toast, "Success", "Password changed successfully!");
      onOpenChange(false);
      setPasswordChange({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      handleApiError("Change Password", error, toast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px] rounded-lg shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-gray-900 dark:text-white">
            Change Password
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Enter your current password and a new password
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            className="w-full"
            type="password"
            placeholder="Current Password"
            value={passwordChange.current_password}
            onChange={(e) =>
              setPasswordChange({
                ...passwordChange,
                current_password: e.target.value,
              })
            }
          />
          <Input
            className="w-full"
            type="password"
            placeholder="New Password"
            value={passwordChange.new_password}
            onChange={(e) =>
              setPasswordChange({
                ...passwordChange,
                new_password: e.target.value,
              })
            }
          />
          <Input
            className="w-full"
            type="password"
            placeholder="Confirm New Password"
            value={passwordChange.confirm_password}
            onChange={(e) =>
              setPasswordChange({
                ...passwordChange,
                confirm_password: e.target.value,
              })
            }
          />
          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
        </div>
        <Button
          className="w-full mt-4"
          onClick={handleChangePassword}
          disabled={loading || !!error}
        >
          {loading ? "Changing..." : "Change Password"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
